import React, { useState, useEffect, useRef } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import AddressAutocomplete from '../components/clients/AddressAutocomplete';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { DateInput } from '../components/ui/date-input';
import { Checkbox } from '../components/ui/checkbox';
import { calcolaCodiceFiscale } from '../utils/fiscalCode';
import { emptyClient } from '../constants/clientConstants';
import ClientCompletionBanner from '../components/clients/ClientCompletionBanner';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Trash2, Pencil, Copy, Calculator, Home, AlertTriangle, FileText, UserPlus, Upload, Sparkles, Loader2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const RUOLO_OPTIONS = ['Proprietario', 'Usufruttuario', 'Locatore', 'Comodatario'];

// Comune di default: JV Service opera quasi esclusivamente su Prato, unico comune per ora
// coperto dalla rubrica aliquote — precompilarlo evita all'operatore di doverlo scrivere ogni
// volta solo per far scattare l'aliquota automatica (resta comunque modificabile a mano).
const DEFAULT_COMUNE = 'PRATO';

const emptyForm = {
  anno: new Date().getFullYear(),
  comune: DEFAULT_COMUNE,
  categoria_imu: '',
  categoria_catastale: '',
  rendita_catastale: '',
  valore_venale: '',
  aliquota_imu: '',
  aliquota_acconto: '',
  quota_possesso: '100',
  mesi_possesso: 12,
  data_inizio_possesso: '',
  includi_acconto: true,
  immobile_storico_inagibile: false,
  ruolo: 'Proprietario',
  descrizione: '',
  notes: '',
};

// Replica in JS la stessa formula del backend (routes/imu.py:_calcola_imu) per un'anteprima
// live del calcolo mentre si compila il form, prima ancora di salvare — il valore definitivo
// resta comunque quello ricalcolato dal backend al salvataggio.
// Mesi di possesso tra gennaio e giugno (stessa regola del server: il mese conta se il possesso supera
// la metà dei giorni, il giorno del passaggio è dell'acquirente; senza data = dall'inizio dell'anno)
const mesiPrimoSemestre = (dataInizio, mesi) => {
  const m = Math.max(0, Math.min(12, mesi || 0));
  if (!m) return 0;
  let primo = 1;
  const r = /^(\d{4})-(\d{2})-(\d{2})/.exec(dataInizio || '');
  if (r) {
    const [anno, mese, giorno] = [+r[1], +r[2], +r[3]];
    const giorniMese = new Date(anno, mese, 0).getDate();
    primo = (giorniMese - giorno + 1) > giorniMese / 2 ? mese : mese + 1;
  }
  return Math.max(0, Math.min(6, Math.min(12, primo + m - 1)) - primo + 1);
};

const calcolaImuLocale = (form, categorie) => {
  const categoria = categorie.find(c => c.key === form.categoria_imu);
  const rendita = parseFloat(String(form.rendita_catastale).replace(',', '.')) || 0;
  const valoreVenale = parseFloat(String(form.valore_venale).replace(',', '.')) || 0;
  const aliquotaImu = parseFloat(String(form.aliquota_imu).replace(',', '.')) || 0;
  const aliquotaAcconto = parseFloat(String(form.aliquota_acconto).replace(',', '.')) || aliquotaImu;
  const quotaPossesso = parseFloat(String(form.quota_possesso).replace(',', '.')) || 0;
  const mesiPossesso = parseInt(form.mesi_possesso, 10) || 0;

  let baseImponibile = 0;
  if (categoria && categoria.base === 'valore_venale') {
    baseImponibile = valoreVenale;
  } else if (categoria) {
    baseImponibile = rendita * categoria.rivalutazione * categoria.moltiplicatore;
  }
  if (form.immobile_storico_inagibile) baseImponibile *= 0.5;

  const fattore = (quotaPossesso / 100) * (mesiPossesso / 12);
  const impostaAnnua = baseImponibile * (aliquotaImu / 100) * fattore;
  const fattoreAcconto = form.includi_acconto ? (quotaPossesso / 100) * (mesiPrimoSemestre(form.data_inizio_possesso, mesiPossesso) / 12) : 0;
  const acconto = baseImponibile * (aliquotaAcconto / 100) * fattoreAcconto;
  const saldo = impostaAnnua - acconto;

  return {
    base_imponibile: Math.round(baseImponibile * 100) / 100,
    imposta_annua: Math.round(impostaAnnua * 100) / 100,
    acconto: Math.round(acconto * 100) / 100,
    saldo: Math.round(saldo * 100) / 100,
  };
};

const formatEuro = (v) => `€ ${(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Mappa il tipo documento scelto sul document_type richiesto da /api/ocr/extract-client-data
const OCR_DOC_TYPE_MAP = {
  "Carta d'identità elettronica": 'carta_identita',
  'Patente': 'patente',
  'Passaporto': 'passaporto',
  'Permesso di soggiorno': 'permesso_soggiorno',
};

// Etichetta breve con cui il documento caricato viene salvato tra i "Documenti anagrafici"
// del cliente (stessa convenzione di Canone RAI/Dimissioni/Ospitalità).
const DOC_LABEL_MAP = {
  "Carta d'identità elettronica": 'CIE',
  'Patente': 'PAT',
  'Passaporto': 'PASS',
  'Permesso di soggiorno': 'PSE',
};

const NEW_CLIENT_DOC_TIPO_OPTIONS = Object.keys(OCR_DOC_TYPE_MAP);

const formatDataIt = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '');

// Scadenza della rata: 16 giugno (acconto) / 16 dicembre (saldo), al lunedì se cade nel weekend (come il server)
const scadenzaRata = (anno, rata) => {
  const d = new Date(anno, rata === 'acconto' ? 5 : 11, 16);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
};

// Stato di una rata nella tabella calcoli: pagata, da pagare, oppure scaduta (→ ravvedimento, cliccabile)
const StatoRata = ({ pagato, anno, rata, importo, onApri }) => {
  if (pagato) {
    return (
      <div className="text-xs text-emerald-700 font-medium">
        Pagato il {formatDataIt(pagato.data_pagamento)} · {formatEuro(pagato.importo)}
      </div>
    );
  }
  if (!importo) return <div className="text-xs text-slate-400">Niente da pagare</div>;
  const scad = scadenzaRata(anno, rata);
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
  if (oggi > scad) {
    return (
      <button type="button" onClick={onApri} className="text-xs text-red-700 font-medium underline hover:text-red-800 text-left">
        Scaduta il {scad.toLocaleDateString('it-IT')} · ravvedimento
      </button>
    );
  }
  return <div className="text-xs text-slate-400">Da pagare entro il {scad.toLocaleDateString('it-IT')}</div>;
};

const Imu = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorie, setCategorie] = useState([]);
  const [clients, setClients] = useState([]);
  const [yearFilter, setYearFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [clientAssets, setClientAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [aliquoteNote, setAliquoteNote] = useState(''); // avviso quando il comune non è in rubrica
  const [rubrica, setRubrica] = useState(null); // { comune, anno, anno_rubrica, aliquote } del comune nel form
  const rubricaTimeout = useRef(null);
  const [comuniSuggeriti, setComuniSuggeriti] = useState([]);
  const [showComuniDropdown, setShowComuniDropdown] = useState(false);
  const [statoAliquote, setStatoAliquote] = useState(null);
  const [saving, setSaving] = useState(false);

  // Dialog "Pagamento e F24"
  const [pagamentoRecord, setPagamentoRecord] = useState(null);
  const [dataPagamento, setDataPagamento] = useState('');
  const [pagamento, setPagamento] = useState(null); // risposta di /imu/{id}/pagamento
  const [rateSel, setRateSel] = useState({ acconto: true, saldo: true });
  const [loadingPagamento, setLoadingPagamento] = useState(false);

  // Nuovo cliente inline con estrazione dati dal documento d'identità (stesso flusso di Canone RAI)
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState(emptyClient);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientDocTipo, setNewClientDocTipo] = useState("Carta d'identità elettronica");
  const [extractingNewClientDoc, setExtractingNewClientDoc] = useState(false);
  // Documento caricato mentre il cliente non esiste ancora: si allega in anagrafica
  // DOPO la creazione del cliente (vedi saveNewClient).
  const [pendingNewClientDoc, setPendingNewClientDoc] = useState(null); // { file, label, expiryDate }

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const params = {};
      if (yearFilter !== 'all') params.anno = yearFilter;
      const res = await axios.get(`${API}/imu`, { params });
      setRecords(res.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento dei calcoli IMU');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data || []);
    } catch (error) {
      // silenzioso: la ricerca cliente resterà vuota finché non si riprova
    }
  };

  const fetchCategorie = async () => {
    try {
      const res = await axios.get(`${API}/imu/categorie`);
      setCategorie(res.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento delle categorie IMU');
    }
  };

  useEffect(() => { fetchClients(); fetchCategorie(); fetchStatoAliquote(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRecords(); }, [yearFilter]);

  const handleClientSearch = (term) => {
    setClientSearchTerm(term);
    setShowClientDropdown(true);
    if (!term.trim()) {
      setFilteredClients(clients);
      return;
    }
    const filtered = clients.filter(c =>
      `${c.last_name} ${c.first_name}`.toLowerCase().includes(term.toLowerCase()) ||
      c.fiscal_code?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredClients(filtered);
  };

  const fetchClientAssets = async (clientId) => {
    try {
      const res = await axios.get(`${API}/assets`, { params: { client_id: clientId, asset_type: 'casa' } });
      setClientAssets(res.data || []);
    } catch (error) {
      setClientAssets([]);
    }
  };

  const selectClient = (client) => {
    setSelectedClientId(client.id);
    setClientSearchTerm(`${client.last_name} ${client.first_name}`);
    setShowClientDropdown(false);
    setSelectedAssetId('');
    fetchClientAssets(client.id);
  };

  // ============ NUOVO CLIENTE INLINE (estrazione OCR documento) =============
  const openNewClient = () => {
    setNewClient(emptyClient);
    setNewClientDocTipo("Carta d'identità elettronica");
    setPendingNewClientDoc(null);
    setNewClientDialogOpen(true);
  };

  // Pulsante "Calcola CF" del form nuovo cliente (stesso di Anagrafica Clienti): calcola il CF
  // dai dati anagrafici e avvisa se appartiene già a un cliente registrato.
  const handleCalcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClient);
      setNewClient(prev => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) {
        toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      } else {
        toast.success('Codice Fiscale calcolato');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Prova ad abbinare un cliente esistente in anagrafica tramite codice fiscale estratto
  const matchClientByCf = (cf) => {
    if (!cf) return null;
    return clients.find(c => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null;
  };

  // Allega il file caricato ai "Documenti anagrafici" del cliente, rinominato come
  // "SIGLA COGNOME NOME" (es. "CIE ROSSI MARIO") — stessa convenzione di Dimissioni/Ospitalità.
  // Se esiste già un documento dello stesso tipo (stessa sigla) in anagrafica, lo sostituisce
  // invece di aggiungerne un altro — la registrazione di un tipo documento va fatta una sola volta.
  const uploadClientDocument = async (clientId, file, shortLabel, lastName, firstName, expiryDate) => {
    try {
      const label = `${shortLabel} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        const existing = (clientRes.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${shortLabel} `)
        );
        if (existing) {
          await axios.delete(`${API}/clients/${clientId}/documents/${existing.id}`);
        }
      } catch (_) {
        // non bloccare l'upload se il controllo duplicati fallisce
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati estratti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  const handleExtractNewClientDoc = async (file) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[newClientDocTipo];
    const docLabel = DOC_LABEL_MAP[newClientDocTipo] || ocrType.toUpperCase();
    setExtractingNewClientDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      if (!c.fiscal_code && !c.first_name) {
        toast.error('Nessun dato riconosciuto nel documento — verifica di aver scelto il tipo documento corretto');
        return;
      }

      const up = (v) => (v || '').toUpperCase();
      let merged = {
        ...newClient,
        first_name: up(c.first_name) || newClient.first_name,
        last_name: up(c.last_name) || newClient.last_name,
        fiscal_code: c.fiscal_code || newClient.fiscal_code,
        gender: c.gender || newClient.gender,
        birth_date: c.birth_date || newClient.birth_date,
        birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClient.birth_abroad,
        birth_comune: up(c.birth_comune) || newClient.birth_comune,
        birth_provincia: c.birth_provincia || newClient.birth_provincia,
        birth_country: up(c.birth_country) || newClient.birth_country,
        address: up(c.address) || newClient.address,
        address_number: c.address_number || newClient.address_number,
        city: up(c.city) || newClient.city,
        city_provincia: c.city_provincia || newClient.city_provincia,
      };

      // CAP non riportato sul documento (le CIE italiane non lo stampano nell'indirizzo) —
      // se comune e provincia di residenza sono noti e individuano un solo CAP possibile,
      // lo compiliamo in automatico invece di lasciarlo vuoto.
      if (!merged.postal_code && merged.city && merged.city_provincia) {
        try {
          const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
          if (capRes.data.single && capRes.data.auto_cap) {
            merged = { ...merged, postal_code: capRes.data.auto_cap };
          }
        } catch (e) {
          // il CAP è un aiuto in più: se il lookup fallisce non deve bloccare il resto del flusso
        }
      }

      // Il documento (es. Passaporto) spesso non riporta il CF italiano — se ora abbiamo
      // tutti i dati per calcolarlo, lo calcoliamo in automatico invece di lasciarlo vuoto.
      const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
        ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
      if (canCalcCf) {
        try {
          const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
            surname: merged.last_name,
            name: merged.first_name,
            birth_date: merged.birth_date,
            gender: merged.gender,
            birth_abroad: merged.birth_abroad,
            birth_comune: merged.birth_comune,
            birth_provincia: merged.birth_provincia,
            birth_country: merged.birth_country,
          });
          if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
        } catch (e) {
          // il calcolo CF è un aiuto in più: se fallisce non deve bloccare il resto del flusso
        }
      }

      // Il CF appena calcolato (o quello letto dal documento) potrebbe corrispondere a un
      // cliente già esistente — evita di creare un duplicato.
      const match = matchClientByCf(merged.fiscal_code);
      if (match) {
        selectClient(match);
        setNewClient(merged);
        await uploadClientDocument(match.id, file, docLabel, match.last_name, match.first_name, d.expiry_date);
        toast.success(`Cliente esistente abbinato: ${match.first_name} ${match.last_name} — documento allegato in anagrafica. Controlla i dati e chiudi quando hai verificato.`);
        // Il dialog resta aperto apposta: l'operatore deve poter controllare i dati estratti
        // prima di chiuderlo (bottone "Annulla"), non si chiude più da solo.
      } else {
        setPendingNewClientDoc({ file, label: docLabel, expiryDate: d.expiry_date || '' });
        setNewClient(merged);
        toast.success('Dati estratti — rivedi e completa la creazione (il documento si allega automaticamente)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal documento');
    } finally {
      setExtractingNewClientDoc(false);
    }
  };

  const saveNewClient = async () => {
    if (!newClient.last_name || !newClient.first_name) {
      toast.error('Cognome e Nome obbligatori');
      return;
    }
    if (!newClient.birth_abroad && (!newClient.birth_comune || !newClient.birth_provincia)) {
      toast.error('Compila Comune e Provincia di nascita (o attiva "Nato/a all\'estero")');
      return;
    }
    if (newClient.birth_abroad && !newClient.birth_country) {
      toast.error('Seleziona la Nazione di nascita');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await axios.post(`${API}/clients`, newClient);
      const created = res.data;
      if (pendingNewClientDoc) {
        await uploadClientDocument(created.id, pendingNewClientDoc.file, pendingNewClientDoc.label, newClient.last_name, newClient.first_name, pendingNewClientDoc.expiryDate);
        setPendingNewClientDoc(null);
      }
      await fetchClients();
      selectClient(created);
      toast.success('Cliente creato');
      setNewClientDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione cliente');
    } finally {
      setCreatingClient(false);
    }
  };

  // Rubrica aliquote del comune/anno nel form: caricata appena il comune è noto (anche
  // all'apertura, con PRATO precompilato), così scegliendo la tipologia l'aliquota si compila
  // subito e il menu Tipologia mostra già l'aliquota di ogni voce.
  const loadRubrica = async (comune, anno, categoriaImu) => {
    if (!comune || !anno) {
      setRubrica(null);
      setAliquoteNote('');
      return;
    }
    try {
      const res = await axios.get(`${API}/imu/aliquote`, { params: { comune, anno } });
      const data = res.data.codice_catastale && Object.keys(res.data.aliquote || {}).length
        ? {
          comune,
          anno,
          nome: res.data.comune || comune,
          provincia: res.data.provincia || '',
          anno_rubrica: res.data.anno_rubrica || anno,
          aliquote: res.data.aliquote,
          agevolazioni: res.data.agevolazioni || [],
          delibera: res.data.delibera || '',
        }
        : null;
      setRubrica(data);
      // categoriaImu === null: solo caricamento (es. apertura in modifica), non tocca aliquote e avvisi
      if (categoriaImu !== null) applyAliquota(data, comune, categoriaImu);
    } catch (error) {
      // l'aliquota resta comunque modificabile a mano
    }
  };

  // Compila Aliquota IMU/acconto dalla rubrica per la tipologia scelta (resta modificabile a mano)
  const applyAliquota = (data, comune, categoriaImu) => {
    if (!data) {
      setAliquoteNote(`Comune "${comune}" non ancora in rubrica — inserisci l'aliquota a mano.`);
      return;
    }
    const annoNote = data.anno_rubrica !== data.anno
      ? ` (aliquote ${data.anno_rubrica}: quelle del ${data.anno} non sono ancora in rubrica)`
      : '';
    if (!categoriaImu) {
      setAliquoteNote(`Aliquote di ${data.nome} caricate${annoNote} — scegli la tipologia per compilarle.`);
      return;
    }
    const v = data.aliquote[categoriaImu];
    if (v === undefined) {
      setAliquoteNote(`Nessuna aliquota in rubrica per questa tipologia a ${data.nome} — inserisci a mano o scegli un'agevolazione del comune.`);
      return;
    }
    const txt = String(v).replace('.', ',');
    setForm(f => ({ ...f, aliquota_imu: txt, aliquota_acconto: txt }));
    setAliquoteNote(`Aliquota ${data.nome} ${data.anno_rubrica} precompilata${annoNote} — verifica le agevolazioni del comune qui sotto prima di salvare.`);
  };

  const handleComuneChange = (value) => {
    const comune = value.toUpperCase();
    setForm(f => ({ ...f, comune }));
    setShowComuniDropdown(true);
    if (rubricaTimeout.current) clearTimeout(rubricaTimeout.current);
    rubricaTimeout.current = setTimeout(async () => {
      loadRubrica(comune.trim(), form.anno, form.categoria_imu);
      try {
        const res = await axios.get(`${API}/imu/comuni`, { params: { q: comune.trim() } });
        setComuniSuggeriti(res.data || []);
      } catch (error) {
        setComuniSuggeriti([]);
      }
    }, 400);
  };

  const selectComune = (c) => {
    if (rubricaTimeout.current) clearTimeout(rubricaTimeout.current);
    setForm(f => ({ ...f, comune: c.comune }));
    setShowComuniDropdown(false);
    loadRubrica(c.comune, form.anno, form.categoria_imu);
  };

  // Agevolazione del comune scelta dall'elenco del prospetto: usa la sua aliquota
  const usaAgevolazione = (a) => {
    const txt = String(a.aliquota).replace('.', ',');
    setForm(f => ({ ...f, aliquota_imu: txt, aliquota_acconto: txt }));
    setAliquoteNote(`Applicata l'agevolazione del comune (${txt}%) — verifica che il cliente ne abbia i requisiti.`);
  };

  // Stato rubrica aliquote (tutti i comuni, aggiornata dal MEF il 1° e il 16 del mese)
  const fetchStatoAliquote = async () => {
    try {
      const res = await axios.get(`${API}/imu/aliquote-stato`);
      setStatoAliquote(res.data);
    } catch (error) {
      // informativo: se non disponibile non si mostra
    }
  };

  const aggiornaAliquoteOra = async () => {
    try {
      const res = await axios.post(`${API}/imu/aliquote-aggiorna`);
      toast.success(res.data.messaggio);
      setTimeout(fetchStatoAliquote, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'avvio dell\'aggiornamento');
    }
  };

  const selectAsset = (assetId) => {
    setSelectedAssetId(assetId);
    if (!assetId) return;
    const asset = clientAssets.find(a => a.id === assetId);
    if (!asset) return;
    const newForm = {
      ...form,
      comune: (asset.city || form.comune).toUpperCase(),
      categoria_catastale: asset.categoria_catastale || form.categoria_catastale,
      rendita_catastale: asset.rendita_catastale || form.rendita_catastale,
    };
    setForm(newForm);
    loadRubrica(newForm.comune, form.anno, newForm.categoria_imu);
  };

  const openNew = () => {
    setEditingRecord(null);
    setForm(emptyForm);
    setSelectedClientId('');
    setClientSearchTerm('');
    setSelectedAssetId('');
    setClientAssets([]);
    setAliquoteNote('');
    setRubrica(null);
    loadRubrica(emptyForm.comune, emptyForm.anno, '');
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setForm({
      anno: record.anno,
      comune: record.comune || '',
      categoria_imu: record.categoria_imu || '',
      categoria_catastale: record.categoria_catastale || '',
      rendita_catastale: record.rendita_catastale || '',
      valore_venale: record.valore_venale || '',
      aliquota_imu: record.aliquota_imu || '',
      aliquota_acconto: record.aliquota_acconto || '',
      quota_possesso: record.quota_possesso ?? 100,
      mesi_possesso: record.mesi_possesso ?? 12,
      data_inizio_possesso: record.data_inizio_possesso || '',
      includi_acconto: record.includi_acconto !== false,
      immobile_storico_inagibile: !!record.immobile_storico_inagibile,
      ruolo: record.ruolo || 'Proprietario',
      descrizione: record.descrizione || '',
      notes: record.notes || '',
    });
    setSelectedClientId(record.client_id);
    setClientSearchTerm(record.client_name || '');
    setSelectedAssetId(record.asset_id || '');
    setAliquoteNote('');
    // solo per mostrare le aliquote nel menu Tipologia: non tocca quelle già salvate nel calcolo
    setRubrica(null);
    loadRubrica(record.comune || '', record.anno, null);
    fetchClientAssets(record.client_id);
    setDialogOpen(true);
  };

  const duplicaAnnoSuccessivo = (record) => {
    openEdit(record);
    setEditingRecord(null); // duplica: salva come nuovo record, non sovrascrive l'originale
    setForm(f => ({ ...f, anno: record.anno + 1 }));
    // aliquote aggiornate al nuovo anno (se in rubrica, altrimenti le ultime disponibili)
    loadRubrica(record.comune || '', record.anno + 1, record.categoria_imu);
  };

  const handleCategoriaChange = (categoriaImu) => {
    setForm(f => ({ ...f, categoria_imu: categoriaImu }));
    if (rubrica && rubrica.comune === form.comune && rubrica.anno === form.anno) {
      applyAliquota(rubrica, form.comune, categoriaImu);
    } else {
      loadRubrica(form.comune || DEFAULT_COMUNE, form.anno, categoriaImu);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      toast.error('Seleziona un cliente');
      return;
    }
    if (!form.categoria_imu) {
      toast.error('Seleziona la tipologia IMU');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: selectedClientId,
        asset_id: selectedAssetId || '',
        anno: parseInt(form.anno, 10),
        comune: form.comune.toUpperCase(),
        categoria_imu: form.categoria_imu,
        categoria_catastale: form.categoria_catastale.toUpperCase(),
        rendita_catastale: parseFloat(String(form.rendita_catastale).replace(',', '.')) || 0,
        valore_venale: parseFloat(String(form.valore_venale).replace(',', '.')) || 0,
        aliquota_imu: parseFloat(String(form.aliquota_imu).replace(',', '.')) || 0,
        aliquota_acconto: parseFloat(String(form.aliquota_acconto).replace(',', '.')) || 0,
        quota_possesso: parseFloat(String(form.quota_possesso).replace(',', '.')) || 100,
        mesi_possesso: parseInt(form.mesi_possesso, 10) || 12,
        data_inizio_possesso: form.data_inizio_possesso,
        includi_acconto: form.includi_acconto,
        immobile_storico_inagibile: form.immobile_storico_inagibile,
        ruolo: form.ruolo,
        descrizione: form.descrizione.toUpperCase(),
        notes: form.notes,
      };
      if (editingRecord) {
        await axios.put(`${API}/imu/${editingRecord.id}`, payload);
        toast.success('Calcolo aggiornato');
      } else {
        await axios.post(`${API}/imu`, payload);
        toast.success('Calcolo salvato');
      }
      setDialogOpen(false);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (error) {
      toast.error('F24 non disponibile');
    }
  };

  // ============ PAGAMENTO E F24 (scadenze, ravvedimento operoso) =============
  const oggiIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const openPagamento = (record) => {
    setPagamentoRecord(record);
    setDataPagamento(oggiIso());
    setRateSel({ acconto: !record.pagamenti?.acconto, saldo: !record.pagamenti?.saldo });
    setPagamento(null);
  };

  // Ricalcola scadenze/ravvedimento lato server a ogni cambio della data di pagamento
  useEffect(() => {
    if (!pagamentoRecord || !dataPagamento) return;
    let annullato = false;
    setLoadingPagamento(true);
    axios.get(`${API}/imu/${pagamentoRecord.id}/pagamento`, { params: { data_pagamento: dataPagamento } })
      .then(res => { if (!annullato) setPagamento(res.data); })
      .catch(err => { if (!annullato) toast.error(err.response?.data?.detail || 'Errore nel calcolo del pagamento'); })
      .finally(() => { if (!annullato) setLoadingPagamento(false); });
    return () => { annullato = true; };
  }, [pagamentoRecord, dataPagamento]);

  const rataPagata = (rata) => pagamentoRecord?.pagamenti?.[rata];
  const righeSelezionate = (pagamento?.righe || []).filter(r => rateSel[r.rata] && !rataPagata(r.rata));

  // Registra il pagamento delle rate selezionate alla data scelta: gli importi restano salvati
  // nel calcolo e non vengono più ricalcolati.
  const registraPagamento = async () => {
    const rate = ['acconto', 'saldo'].filter(r => rateSel[r] && !rataPagata(r));
    if (!rate.length) {
      toast.error('Seleziona almeno una rata da registrare');
      return;
    }
    const descr = rate.map(r => (r === 'acconto' ? 'Acconto' : 'Saldo')).join(' e ');
    if (!window.confirm(`Registrare il pagamento di ${descr} il ${formatDataIt(dataPagamento)} per ${formatEuro(totaleSelezionato)}?`)) return;
    try {
      const res = await axios.post(`${API}/imu/${pagamentoRecord.id}/pagamenti`, { rate, data_pagamento: dataPagamento });
      toast.success('Pagamento registrato');
      setRateSel({ acconto: false, saldo: false });
      setPagamentoRecord(res.data);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione del pagamento');
    }
  };

  const annullaPagamento = async (rata) => {
    if (!window.confirm(`Annullare il pagamento registrato del ${rata === 'acconto' ? 'Acconto' : 'Saldo'}?`)) return;
    try {
      const res = await axios.delete(`${API}/imu/${pagamentoRecord.id}/pagamenti/${rata}`);
      toast.success('Pagamento annullato');
      setRateSel(sel => ({ ...sel, [rata]: true }));
      setPagamentoRecord(res.data);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento del pagamento');
    }
  };
  const totaleSelezionato = righeSelezionate.reduce((sum, r) => sum + r.totale, 0);

  const generaF24 = () => {
    const rate = ['acconto', 'saldo'].filter(r => rateSel[r] && !rataPagata(r));
    if (!rate.length) {
      toast.error('Seleziona almeno una rata');
      return;
    }
    openPdf(`${API}/imu/${pagamentoRecord.id}/f24-pdf?rate=${rate.join(',')}&data_pagamento=${dataPagamento}`, 'f24_imu.pdf');
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Eliminare il calcolo IMU ${record.anno} di ${record.client_name}?`)) return;
    try {
      await axios.delete(`${API}/imu/${record.id}`);
      toast.success('Calcolo eliminato');
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const anteprima = calcolaImuLocale(form, categorie);

  // Data di pagamento nel modulo: il server calcola le rate da versare a quella data (ravvedimento
  // compreso se è dopo la scadenza), anche prima di salvare
  const [dataPagForm, setDataPagForm] = useState('');
  const [pagForm, setPagForm] = useState(null);
  useEffect(() => { if (dialogOpen) setDataPagForm(oggiIso()); }, [dialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dialogOpen || !form.categoria_imu || !dataPagForm || !form.anno) { setPagForm(null); return undefined; }
    const num = (v, d = 0) => parseFloat(String(v).replace(',', '.')) || d;
    const t = setTimeout(() => {
      axios.post(`${API}/imu/anteprima-pagamento`, {
        anno: parseInt(form.anno, 10), comune: (form.comune || '').toUpperCase(), categoria_imu: form.categoria_imu,
        rendita_catastale: num(form.rendita_catastale), valore_venale: num(form.valore_venale), aliquota_imu: num(form.aliquota_imu),
        aliquota_acconto: num(form.aliquota_acconto), quota_possesso: num(form.quota_possesso, 100),
        mesi_possesso: parseInt(form.mesi_possesso, 10) || 12, data_inizio_possesso: form.data_inizio_possesso || '',
        includi_acconto: form.includi_acconto, immobile_storico_inagibile: form.immobile_storico_inagibile,
        data_pagamento: dataPagForm,
      }).then((r) => setPagForm(r.data)).catch(() => setPagForm(null));
    }, 400);
    return () => clearTimeout(t);
  }, [dialogOpen, form, dataPagForm]);
  const categoriaSelezionata = categorie.find(c => c.key === form.categoria_imu);
  const isAreaFabbricabile = categoriaSelezionata?.base === 'valore_venale';

  const filteredRecords = records.filter((r) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (r.client_name || '').toLowerCase().includes(q)
      || (r.client_fiscal_code || '').toLowerCase().includes(q)
      || (r.comune || '').toLowerCase().includes(q);
  });

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calcolo IMU</h1>
          <p className="text-slate-500 text-sm mt-1">
            Calcolo dell'Imposta Municipale Propria sui Beni Immobili dei clienti.
          </p>
          {statoAliquote && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
              Aliquote comunali dal MEF:{' '}
              {Object.entries(statoAliquote.comuni_per_anno || {}).map(([anno, n]) => `${n} comuni ${anno}`).join(' · ')}
              {statoAliquote.ultimo_aggiornamento && ` · aggiornate il ${new Date(statoAliquote.ultimo_aggiornamento).toLocaleDateString('it-IT')}`}
              {statoAliquote.in_corso ? (
                <span className="inline-flex items-center gap-1 text-sky-700"><Loader2 className="w-3 h-3 animate-spin" /> aggiornamento in corso</span>
              ) : (
                <button type="button" className="underline hover:text-slate-800" onClick={aggiornaAliquoteOra}>aggiorna ora</button>
              )}
            </p>
          )}
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-imu-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo calcolo
        </Button>
      </div>

      {/* Filtri — stessa barra di ricerca di Canone RAI */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome cliente, codice fiscale o comune..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-imu"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-sm text-slate-500">Anno</Label>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Comune</TableHead>
                <TableHead>Anno</TableHead>
                <TableHead>Tipologia</TableHead>
                <TableHead>Importo annuo</TableHead>
                <TableHead>Acconto</TableHead>
                <TableHead>Saldo</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : filteredRecords.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  {records.length === 0 ? 'Nessun calcolo IMU salvato' : 'Nessun calcolo trovato per la ricerca'}
                </TableCell></TableRow>
              ) : (
                filteredRecords.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="font-medium">{r.client_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.client_fiscal_code}</div>
                    </TableCell>
                    <TableCell>{r.comune}</TableCell>
                    <TableCell>{r.anno}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{categorie.find(c => c.key === r.categoria_imu)?.label || r.categoria_imu}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{formatEuro(r.imposta_annua)}</TableCell>
                    <TableCell>{formatEuro(r.acconto)}<StatoRata pagato={r.pagamenti?.acconto} anno={r.anno} rata="acconto" importo={r.acconto} onApri={() => openPagamento(r)} /></TableCell>
                    <TableCell>{formatEuro(r.saldo)}<StatoRata pagato={r.pagamenti?.saldo} anno={r.anno} rata="saldo" importo={r.saldo} onApri={() => openPagamento(r)} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" className="mr-1" onClick={() => openPagamento(r)} title="Data di pagamento, ravvedimento e F24">
                        <FileText className="w-4 h-4 mr-1" /> F24
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(r)}>
                            <Pencil className="w-4 h-4 mr-2" /> Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => duplicaAnnoSuccessivo(r)}>
                            <Copy className="w-4 h-4 mr-2" /> Duplica per {r.anno + 1}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openPagamento(r)}>
                            <FileText className="w-4 h-4 mr-2" /> Pagamento e F24
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(r)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Modifica calcolo IMU' : 'Nuovo calcolo IMU'}</DialogTitle>
            <DialogDescription>
              Le aliquote sono comunali e cambiano ogni anno: quando il comune non è ancora in rubrica, o per casi particolari (agevolazioni specifiche), inserisci/correggi l'aliquota a mano.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2 relative">
              <div className="flex items-center justify-between">
                <Label>Cliente *</Label>
                {!editingRecord && (
                  <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs gap-1" onClick={openNewClient}>
                    <UserPlus className="w-3.5 h-3.5" />
                    Nuovo cliente
                  </Button>
                )}
              </div>
              <Input
                placeholder="Cerca cliente per nome o codice fiscale..."
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientDropdown(true)}
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                disabled={!!editingRecord}
              />
              {showClientDropdown && !editingRecord && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                  {filteredClients.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">Nessun cliente trovato</div>
                  ) : (
                    filteredClients.slice(0, 10).map((client) => (
                      <div
                        key={client.id}
                        onMouseDown={(e) => { e.preventDefault(); selectClient(client); }}
                        className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${selectedClientId === client.id ? 'bg-blue-50 border-blue-200' : ''}`}
                      >
                        <div className="font-medium">{client.last_name} {client.first_name}</div>
                        <div className="text-sm text-slate-500 font-mono">{client.fiscal_code}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <ClientCompletionBanner
              client={clients.find(c => c.id === selectedClientId)}
              onSaved={(updated) => setClients(prev => prev.map(c => c.id === updated.id ? updated : c))}
            />

            {selectedClientId && (
              <div className="space-y-2">
                <Label>Bene Immobile (opzionale — precompila comune/categoria/rendita)</Label>
                <Select value={selectedAssetId} onValueChange={selectAsset}>
                  <SelectTrigger>
                    <SelectValue placeholder={clientAssets.length ? "Seleziona un bene immobile..." : "Nessun bene immobile in anagrafica"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clientAssets.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <Home className="w-3.5 h-3.5 inline mr-1" />
                        {a.indirizzo} {a.numero_civico} — {a.city} {a.categoria_catastale ? `(${a.categoria_catastale})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Anno *</Label>
                <Select value={form.anno.toString()} onValueChange={(v) => { setForm(f => ({ ...f, anno: parseInt(v, 10) })); loadRubrica(form.comune || DEFAULT_COMUNE, parseInt(v, 10), form.categoria_imu); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Comune</Label>
                <div className="relative">
                  <Input
                    value={form.comune}
                    onChange={(e) => handleComuneChange(e.target.value)}
                    onFocus={() => setShowComuniDropdown(true)}
                    onBlur={() => setTimeout(() => setShowComuniDropdown(false), 200)}
                    placeholder="es. PRATO"
                  />
                  {showComuniDropdown && comuniSuggeriti.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-56 overflow-y-auto bg-white shadow-lg">
                      {comuniSuggeriti.map((c) => (
                        <div
                          key={c.codice_catastale}
                          onMouseDown={(e) => { e.preventDefault(); selectComune(c); }}
                          className="px-3 py-2 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 text-sm"
                        >
                          {c.comune} <span className="text-slate-400">({c.provincia}) · {c.codice_catastale}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tipologia immobile (categoria IMU) *</Label>
              <Select value={form.categoria_imu} onValueChange={handleCategoriaChange}>
                <SelectTrigger><SelectValue placeholder="Seleziona tipologia..." /></SelectTrigger>
                <SelectContent>
                  {categorie.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {c.label}
                      {rubrica?.aliquote?.[c.key] !== undefined && (
                        <span className="ml-1 font-semibold text-emerald-700">— {String(rubrica.aliquote[c.key]).replace('.', ',')}%</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {aliquoteNote && (
                <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {aliquoteNote}</p>
              )}
              {rubrica?.agevolazioni?.length > 0 && (
                <details className="border rounded-lg bg-slate-50 text-sm">
                  <summary className="cursor-pointer px-3 py-2 font-medium text-slate-700">
                    Agevolazioni del comune di {rubrica.nome} ({rubrica.agevolazioni.length})
                    {rubrica.delibera && <span className="font-normal text-slate-500"> · delibera {rubrica.delibera}</span>}
                  </summary>
                  <div className="divide-y border-t">
                    {rubrica.agevolazioni.map((a, i) => (
                      <div key={i} className="flex items-start gap-3 px-3 py-2">
                        <span className="font-semibold text-emerald-700 whitespace-nowrap">{String(a.aliquota).replace('.', ',')}%</span>
                        <span className="flex-1 text-xs text-slate-600">{a.descrizione}</span>
                        <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => usaAgevolazione(a)}>Usa</Button>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria catastale</Label>
                <Input
                  value={form.categoria_catastale}
                  onChange={(e) => setForm(f => ({ ...f, categoria_catastale: e.target.value.toUpperCase() }))}
                  placeholder="es. A/2"
                />
              </div>
              {isAreaFabbricabile ? (
                <div className="space-y-2">
                  <Label>Valore venale (€)</Label>
                  <Input value={form.valore_venale} onChange={(e) => setForm(f => ({ ...f, valore_venale: e.target.value }))} placeholder="0,00" />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Rendita catastale (€)</Label>
                  <Input value={form.rendita_catastale} onChange={(e) => setForm(f => ({ ...f, rendita_catastale: e.target.value }))} placeholder="0,00" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Aliquota IMU (%)</Label>
                <Input value={form.aliquota_imu} onChange={(e) => setForm(f => ({ ...f, aliquota_imu: e.target.value }))} placeholder="Scegli la tipologia" />
              </div>
              <div className="space-y-2">
                <Label>Aliquota acconto (%)</Label>
                <Input value={form.aliquota_acconto} onChange={(e) => setForm(f => ({ ...f, aliquota_acconto: e.target.value }))} placeholder="Scegli la tipologia" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quota di possesso (%)</Label>
                <Input value={form.quota_possesso} onChange={(e) => setForm(f => ({ ...f, quota_possesso: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Mesi di possesso</Label>
                <Select value={form.mesi_possesso.toString()} onValueChange={(v) => setForm(f => ({ ...f, mesi_possesso: parseInt(v, 10) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <SelectItem key={m} value={m.toString()}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data inizio possesso</Label>
                <DateInput value={form.data_inizio_possesso} onChange={(v) => setForm(f => ({ ...f, data_inizio_possesso: v }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Includi nell'acconto</Label>
                <Select value={form.includi_acconto ? 'si' : 'no'} onValueChange={(v) => setForm(f => ({ ...f, includi_acconto: v === 'si' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Sì</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Immobile storico / inagibile</Label>
                <Select value={form.immobile_storico_inagibile ? 'si' : 'no'} onValueChange={(v) => setForm(f => ({ ...f, immobile_storico_inagibile: v === 'si' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Sì (riduzione 50% base imponibile)</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Proprietario/Locatore/Comodatario</Label>
              <Select value={form.ruolo} onValueChange={(v) => setForm(f => ({ ...f, ruolo: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RUOLO_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descrizione immobile</Label>
              <Input value={form.descrizione} onChange={(e) => setForm(f => ({ ...f, descrizione: e.target.value.toUpperCase() }))} placeholder="facoltativa" />
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>

            <div className="border rounded-lg p-4 bg-slate-50 space-y-2">
              <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
                <Calculator className="w-4 h-4" /> Anteprima calcolo
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><span className="text-slate-500 block">Base imponibile</span><span className="font-medium">{formatEuro(anteprima.base_imponibile)}</span></div>
                <div><span className="text-slate-500 block">Imposta annua</span><span className="font-medium">{formatEuro(anteprima.imposta_annua)}</span></div>
                <div><span className="text-slate-500 block">Acconto</span><span className="font-medium">{formatEuro(anteprima.acconto)}</span></div>
                <div><span className="text-slate-500 block">Saldo</span><span className="font-medium">{formatEuro(anteprima.saldo)}</span></div>
              </div>
              <div className="border-t pt-3 mt-1 space-y-2">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-xs">Data di pagamento</Label>
                    <Input type="date" value={dataPagForm} onChange={(e) => setDataPagForm(e.target.value)} className="w-44 h-9 bg-white" />
                  </div>
                  {pagForm && <span className="text-xs text-slate-500 pb-2">Se è dopo la scadenza: ravvedimento automatico · tasso legale {String(pagForm.tasso_legale).replace('.', ',')}%</span>}
                </div>
                {pagForm && pagForm.righe.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-slate-500 text-xs text-left">
                        <th className="py-1 pr-2 font-normal">Rata</th><th className="pr-2 font-normal">Scadenza</th><th className="pr-2 font-normal text-right">Dovuto</th>
                        <th className="pr-2 font-normal">Ritardo</th><th className="pr-2 font-normal text-right">Sanzione</th><th className="pr-2 font-normal text-right">Interessi</th>
                        <th className="font-normal text-right">Da versare</th>
                      </tr></thead>
                      <tbody>
                        {pagForm.righe.map((r, i) => (
                          <tr key={`${r.rata}-${r.codice_tributo}-${i}`} className="border-t border-slate-200">
                            <td className="py-1 pr-2">{r.rata === 'acconto' ? 'Acconto' : 'Saldo'} <span className="text-slate-400 font-mono text-xs">{r.codice_tributo}</span></td>
                            <td className="pr-2">{new Date(r.scadenza).toLocaleDateString('it-IT')}</td>
                            <td className="pr-2 text-right">{formatEuro(r.dovuto)}</td>
                            <td className="pr-2">{r.ravvedimento
                              ? <span className="text-red-700">{r.giorni_ritardo} gg · {String(r.sanzione_pct.toFixed(3)).replace(/\.?0+$/, '').replace('.', ',')}%</span>
                              : <span className="text-emerald-700">nei termini</span>}</td>
                            <td className="pr-2 text-right">{r.ravvedimento ? formatEuro(r.sanzione) : '-'}</td>
                            <td className="pr-2 text-right">{r.ravvedimento ? formatEuro(r.interessi) : '-'}</td>
                            <td className="text-right font-semibold">{formatEuro(r.totale)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="text-right font-semibold text-emerald-800 mt-1">Totale da versare: {formatEuro(pagForm.totale)}</div>
                  </div>
                )}
                <p className="text-xs text-slate-500">Dopo aver salvato, il pulsante <b>F24</b> nell'elenco crea il modello e registra il pagamento.</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ========== NUOVO CLIENTE DIALOG (estrazione OCR documento) ========== */}
      <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo cliente</DialogTitle>
            <DialogDescription>Inserisci solo i dati essenziali. Potrai completare l'anagrafica in seguito.</DialogDescription>
          </DialogHeader>

          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
              <Sparkles className="w-4 h-4" />
              Estrazione automatica dati (facoltativa)
            </div>
            <p className="text-xs text-violet-700">
              Carica il documento d'identità: i campi sotto si compilano da soli (puoi comunque correggerli a mano).
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs text-violet-700">Tipo documento che stai per caricare</Label>
              <Select value={newClientDocTipo} onValueChange={setNewClientDocTipo}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NEW_CLIENT_DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingNewClientDoc ? 'opacity-60 pointer-events-none' : ''}`}>
              {extractingNewClientDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Carica {newClientDocTipo}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleExtractNewClientDoc(f);
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cognome *</Label>
              <Input value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs">Sesso</Label>
              <Select value={newClient.gender} onValueChange={(v) => setNewClient({ ...newClient, gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Maschio</SelectItem>
                  <SelectItem value="F">Femmina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data di nascita</Label>
              <DateInput value={newClient.birth_date} onChange={(val) => setNewClient({ ...newClient, birth_date: val })} placeholder="gg/mm/aaaa" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="imu-birth-abroad" checked={newClient.birth_abroad}
                        onCheckedChange={(v) => setNewClient({ ...newClient, birth_abroad: !!v, birth_comune: '', birth_provincia: '', birth_country: '' })} />
              <Label htmlFor="imu-birth-abroad" className="text-sm">Nato/a all'estero</Label>
            </div>
            {newClient.birth_abroad ? (
              <div className="col-span-2">
                <Label className="text-xs">Nazione di nascita</Label>
                <Input value={newClient.birth_country} onChange={(e) => setNewClient({ ...newClient, birth_country: e.target.value.toUpperCase() })} />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Comune di nascita</Label>
                  <Input value={newClient.birth_comune} onChange={(e) => setNewClient({ ...newClient, birth_comune: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label className="text-xs">Provincia (sigla)</Label>
                  <Input maxLength={2} value={newClient.birth_provincia}
                         onChange={(e) => setNewClient({ ...newClient, birth_provincia: e.target.value.toUpperCase() })} />
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label className="text-xs">Codice fiscale</Label>
              <div className="flex gap-2">
                <Input value={newClient.fiscal_code}
                       onChange={(e) => setNewClient({ ...newClient, fiscal_code: e.target.value.toUpperCase() })} />
                <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfDaDati}>Calcola CF</Button>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Indirizzo residenza <span className="text-blue-600">(ricerca Google Maps)</span></Label>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-2">
                  {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <AddressAutocomplete
                        value={newClient.address}
                        defaultValue={newClient.address}
                        placeholder="es. Via Roma, Piazza Duomo..."
                        onPlaceSelect={(d) => setNewClient(prev => ({
                          ...prev,
                          address: (d.address || prev.address || '').toUpperCase(),
                          address_number: (d.address_number || prev.address_number || '').toUpperCase(),
                          city: (d.city || prev.city || '').toUpperCase(),
                          city_provincia: (d.city_provincia || prev.city_provincia || '').toUpperCase(),
                          postal_code: d.postal_code || prev.postal_code,
                        }))}
                      />
                    </APIProvider>
                  ) : (
                    <Input placeholder="Via/Piazza..." value={newClient.address}
                           onChange={(e) => setNewClient({ ...newClient, address: e.target.value.toUpperCase() })} />
                  )}
                </div>
                <Input placeholder="Civico" value={newClient.address_number}
                       onChange={(e) => setNewClient({ ...newClient, address_number: e.target.value.toUpperCase() })} />
                <Input placeholder="CAP" value={newClient.postal_code}
                       onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })} />
                <Input className="col-span-2" placeholder="Città" value={newClient.city}
                       onChange={(e) => setNewClient({ ...newClient, city: e.target.value.toUpperCase() })} />
                <Input placeholder="Prov (sigla)" maxLength={2} value={newClient.city_provincia}
                       onChange={(e) => setNewClient({ ...newClient, city_provincia: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Telefono</Label>
              <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setNewClientDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveNewClient} disabled={creatingClient}>
              {creatingClient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crea e seleziona
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== PAGAMENTO E F24 ========== */}
      <Dialog open={!!pagamentoRecord} onOpenChange={(open) => { if (!open) setPagamentoRecord(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pagamento e F24 — {pagamentoRecord?.client_name}</DialogTitle>
            <DialogDescription>
              IMU {pagamentoRecord?.anno} · {pagamentoRecord?.comune} · imposta annua {formatEuro(pagamentoRecord?.imposta_annua)}.
              Importi arrotondati all'euro; se paghi dopo la scadenza si applica in automatico il ravvedimento
              operoso (sanzione ridotta + interessi al tasso legale), versato con lo stesso codice tributo.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label>Data di pagamento</Label>
              <Input type="date" value={dataPagamento} onChange={(e) => setDataPagamento(e.target.value)} className="w-44" />
            </div>
            {pagamento && (
              <p className="text-xs text-slate-500 pb-2">Tasso legale {String(pagamento.tasso_legale).replace('.', ',')}% annuo</p>
            )}
            {loadingPagamento && <Loader2 className="w-4 h-4 animate-spin text-slate-400 mb-2.5" />}
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Rata</TableHead>
                  <TableHead>Cod. tributo</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead className="text-right">Dovuto</TableHead>
                  <TableHead>Ravvedimento</TableHead>
                  <TableHead className="text-right">Sanzione</TableHead>
                  <TableHead className="text-right">Interessi</TableHead>
                  <TableHead className="text-right">Da versare</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pagamento?.righe || []).map((r, i) => rataPagata(r.rata) ? (
                  <TableRow key={`${r.rata}-${r.codice_tributo}-${i}`} className="bg-emerald-50/60">
                    <TableCell><Check className="w-4 h-4 text-emerald-600" /></TableCell>
                    <TableCell className="font-medium">{r.rata === 'acconto' ? 'IMU Acconto' : 'IMU Saldo'}</TableCell>
                    <TableCell className="font-mono">{r.codice_tributo} <span className="text-slate-400">{r.codice_comune}</span></TableCell>
                    <TableCell>{new Date(r.scadenza).toLocaleDateString('it-IT')}</TableCell>
                    <TableCell colSpan={4} className="text-emerald-800 text-sm">
                      Pagato il {formatDataIt(rataPagata(r.rata).data_pagamento)}
                      {rataPagata(r.rata).ravvedimento && ` (con ravvedimento: sanzione ${formatEuro(rataPagata(r.rata).sanzione)}, interessi ${formatEuro(rataPagata(r.rata).interessi)})`}
                      <button type="button" className="ml-2 text-xs text-slate-500 underline hover:text-red-600" onClick={() => annullaPagamento(r.rata)}>
                        annulla
                      </button>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-emerald-800">
                      {i === (pagamento.righe.findIndex(x => x.rata === r.rata)) ? formatEuro(rataPagata(r.rata).importo) : ''}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={`${r.rata}-${r.codice_tributo}-${i}`} className={rateSel[r.rata] ? '' : 'opacity-50'}>
                    <TableCell>
                      <Checkbox checked={!!rateSel[r.rata]} onCheckedChange={(v) => setRateSel(s => ({ ...s, [r.rata]: !!v }))} />
                    </TableCell>
                    <TableCell className="font-medium">{r.rata === 'acconto' ? 'IMU Acconto' : 'IMU Saldo'}</TableCell>
                    <TableCell className="font-mono">{r.codice_tributo} <span className="text-slate-400">{r.codice_comune}</span></TableCell>
                    <TableCell>{new Date(r.scadenza).toLocaleDateString('it-IT')}</TableCell>
                    <TableCell className="text-right">{formatEuro(r.dovuto)}</TableCell>
                    <TableCell>
                      {r.ravvedimento ? (
                        <Badge className="bg-red-100 text-red-700">
                          {r.giorni_ritardo} gg · {String(r.sanzione_pct.toFixed(3)).replace(/\.?0+$/, '').replace('.', ',')}%
                        </Badge>
                      ) : (
                        <span className="text-emerald-700 text-sm">Nei termini</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{r.ravvedimento ? formatEuro(r.sanzione) : '-'}</TableCell>
                    <TableCell className="text-right">{r.ravvedimento ? formatEuro(r.interessi) : '-'}</TableCell>
                    <TableCell className="text-right font-semibold">{formatEuro(r.totale)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 font-semibold text-emerald-800">
              Da versare: {formatEuro(totaleSelezionato)}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPagamentoRecord(null)}>Chiudi</Button>
              <Button variant="outline" onClick={generaF24} disabled={!righeSelezionate.length || loadingPagamento}>
                <FileText className="w-4 h-4 mr-2" /> Genera F24
              </Button>
              <Button onClick={registraPagamento} disabled={!righeSelezionate.length || loadingPagamento}
                      className="bg-emerald-600 hover:bg-emerald-700">
                <Check className="w-4 h-4 mr-2" /> Registra pagamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Imu;
